import { IconClock, IconSparkles, IconTag } from '@tabler/icons-react';
import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createCheckoutSession } from '@/api/payment';
import {
  prepareGoogleAdsBeginCheckoutTag,
  requestGoogleAdsBeginCheckoutRetry,
  trackGoogleAdsBeginCheckoutBeforeNavigation,
} from '@/analytics/google-ads-begin-checkout';
import { trackRetentionEvent } from '@/analytics/retention-events';
import { authClient } from '@/auth/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCurrentPlan } from '@/hooks/use-payment';
import {
  CHECKOUT_RETURN_OFFER_CAMPAIGN,
  CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT,
  CHECKOUT_RETURN_OFFER_PROMOTION_CODE,
  CHECKOUT_RETURN_STATUS_PARAM,
  type CheckoutReturnOfferMarker,
  clearCheckoutReturnOfferMarker,
  getCheckoutReturnOfferDiscountedAmount,
  hasSuccessfulCheckoutReturn,
  readCheckoutReturnOfferMarker,
} from '@/lib/checkout-return-offer';
import { formatPrice } from '@/lib/formatter';
import { m } from '@/locale/paraglide/messages';

export function CheckoutReturnOfferDialog() {
  const locationHref =
    useRouterState({ select: (state) => state.location.href }) ?? '';
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const currentPlanQuery = useCurrentPlan(Boolean(session?.user));
  const [marker, setMarker] = useState<CheckoutReturnOfferMarker | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const shownTokenRef = useRef<string | null>(null);
  const isEligibilityPending =
    isSessionPending || (Boolean(session?.user) && currentPlanQuery.isPending);
  const isEligible =
    Boolean(session?.user) &&
    currentPlanQuery.data?.monetizationTier === 'free';

  const syncMarker = useCallback(() => {
    if (hasSuccessfulCheckoutReturn(window.location.search)) {
      clearCheckoutReturnOfferMarker();
      setMarker(null);
      return;
    }

    const nextMarker = readCheckoutReturnOfferMarker();
    setMarker(nextMarker);
    if (!nextMarker) return;

    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(CHECKOUT_RETURN_STATUS_PARAM)) {
      currentUrl.searchParams.delete(CHECKOUT_RETURN_STATUS_PARAM);
      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );
    }
  }, []);

  useEffect(() => {
    syncMarker();
  }, [locationHref, syncMarker]);

  useEffect(() => {
    window.addEventListener('pageshow', syncMarker);
    return () => window.removeEventListener('pageshow', syncMarker);
  }, [syncMarker]);

  useEffect(() => {
    if (!marker || isEligibilityPending) return;
    if (!isEligible) {
      clearCheckoutReturnOfferMarker();
      setMarker(null);
      return;
    }
    if (shownTokenRef.current === marker.token) return;
    shownTokenRef.current = marker.token;
    trackRetentionEvent('checkout_return_offer_shown', {
      planId: marker.planId,
      interval: marker.interval,
      originalAmount: marker.originalAmount,
      currency: marker.currency,
    });
  }, [isEligibilityPending, isEligible, marker]);

  function declineOffer() {
    if (marker) {
      trackRetentionEvent('checkout_return_offer_declined', {
        planId: marker.planId,
        interval: marker.interval,
      });
    }
    clearCheckoutReturnOfferMarker();
    setMarker(null);
  }

  async function acceptOffer() {
    if (!marker || isCheckoutLoading) return;
    setIsCheckoutLoading(true);
    prepareGoogleAdsBeginCheckoutTag();
    trackRetentionEvent('checkout_return_offer_accepted', {
      planId: marker.planId,
      interval: marker.interval,
    });

    try {
      const result = await createCheckoutSession({
        data: {
          planId: marker.planId,
          priceId: marker.priceId,
          returnUrl: new URL(
            marker.returnTo,
            window.location.origin
          ).toString(),
          checkoutReturnOfferToken: marker.token,
          metadata: {
            source: 'checkout_return_offer',
            offer: CHECKOUT_RETURN_OFFER_CAMPAIGN,
          },
        },
      });
      if (!result?.url) throw new Error('Checkout redirect URL is missing');

      trackRetentionEvent('begin_checkout', {
        currency: result.beginCheckout.currency,
        paymentScene: 'subscription',
        planId: marker.planId,
        priceId: marker.priceId,
        source: 'checkout_return_offer',
        value: result.beginCheckout.value,
      });

      trackRetentionEvent('checkout_return_offer_checkout_started', {
        planId: marker.planId,
        interval: marker.interval,
      });
      clearCheckoutReturnOfferMarker();
      setMarker(null);
      try {
        await trackGoogleAdsBeginCheckoutBeforeNavigation(result.beginCheckout);
      } catch (error) {
        console.warn('Begin-checkout tracking failed:', error);
      } finally {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Checkout-return offer failed:', error);
      trackRetentionEvent('checkout_start_failed', {
        errorCategory: 'provider',
        paymentScene: 'subscription',
        source: 'checkout_return_offer',
      });
      requestGoogleAdsBeginCheckoutRetry();
      toast.error(m.pricing_checkout_failed());
      setIsCheckoutLoading(false);
    }
  }

  if (!marker || isEligibilityPending || !isEligible) return null;

  const discountedAmount = getCheckoutReturnOfferDiscountedAmount(
    marker.originalAmount
  );
  const originalPrice = formatPrice(marker.originalAmount, marker.currency, {
    preserveCents: true,
  });
  const discountedPrice = formatPrice(discountedAmount, marker.currency, {
    preserveCents: true,
  });
  const intervalLabel = marker.interval === 'year' ? 'yearly' : 'monthly';

  return (
    <Dialog open onOpenChange={(open) => !open && declineOffer()}>
      <DialogContent
        data-testid="checkout-return-offer"
        className="overflow-hidden border-white/10 bg-[#0b0c0d] p-0 text-white sm:max-w-md"
      >
        <div className="border-white/10 border-b bg-[#15120a] px-5 py-5 sm:px-6">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/35 bg-[#d4af37]/10 px-3 py-1 font-semibold text-[#ffe9a8] text-xs">
            <IconTag className="size-4" />
            {CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT}% checkout offer
          </div>
          <DialogHeader>
            <DialogTitle className="font-semibold text-2xl leading-tight text-white">
              Return to checkout and save 15%
            </DialogTitle>
            <DialogDescription className="text-white/62 leading-6">
              Complete your {marker.planName} {intervalLabel} subscription with
              the discount applied automatically.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 px-5 py-5 sm:px-6">
          <div className="flex items-end justify-between gap-4 border-white/10 border-b pb-5">
            <div>
              <p className="font-semibold text-white/55 text-xs uppercase">
                {marker.planName} {intervalLabel}
              </p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-white/42 line-through">
                  {originalPrice}
                </span>
                <strong className="font-semibold text-3xl text-white">
                  {discountedPrice}
                </strong>
              </div>
            </div>
            <IconSparkles className="size-6 shrink-0 text-[#d4af37]" />
          </div>

          <div className="space-y-2 text-sm text-white/68">
            <p className="flex items-center gap-2">
              <IconTag className="size-4 text-[#d4af37]" />
              Code {CHECKOUT_RETURN_OFFER_PROMOTION_CODE} is applied for you
            </p>
            <p className="flex items-center gap-2">
              <IconClock className="size-4 text-[#d4af37]" />
              This one-time offer expires in 30 minutes
            </p>
          </div>
        </div>

        <DialogFooter className="border-white/10 bg-white/[0.03] px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            className="text-white/62 hover:bg-white/8 hover:text-white"
            onClick={declineOffer}
            disabled={isCheckoutLoading}
          >
            Not now
          </Button>
          <Button
            type="button"
            className="bg-[#d4af37] font-semibold text-black hover:bg-[#f0d46c]"
            onClick={acceptOffer}
            disabled={isCheckoutLoading}
          >
            {isCheckoutLoading ? 'Opening checkout...' : 'Apply 15% and pay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
